import type { HTMLAttributes, ReactNode, RefCallback } from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';
import { Text } from '../text/text';

import * as styles from './table-cell.css';

export interface TableHeaderCellProps {
  /**
   * Additional classes to apply.
   */
  className?: string;
  /**
   * Child text/nodes.
   */
  children: NonNullable<ReactNode>;
  /**
   * When the header applies to multiple columns.
   */
  colSpan?: number;
  /**
   * When the header applies to multiple rows.
   **/
  rowSpan?: number;
  /**
   * To help screen readers interpret tables.
   * In the header row, the col value for scope associates each header cell with the data cells in the column.
   * In the header column, the row value associates the individual headers with their rows.
   */
  scope?: 'row' | 'col';
  /**
   * Aligning value based on the value type. E.g. Left align text values and right align number values.
   */
  align?: keyof typeof styles.textAlign;
  screenReaderOnly?: boolean;
  /**
   * Sticks the header cell to the top of the table.
   */
  sticky?: boolean;
  maxWidth?: number;
  /**
   * Pins the header cell - pixel value for the left position
   * 'auto' is a special value used on the initial render to apply partial pinning styles
   */
  pinnedLeft?: number | 'auto';
  /**
   * To help defines the style of the header cell.
   **/
  variant?: 'default' | 'shrink';
  callbackRef?: RefCallback<HTMLTableCellElement>;
}

export const TableHeaderCell = ({
  children,
  colSpan = 1,
  rowSpan = 1,
  scope = 'col',
  align = 'left',
  className,
  screenReaderOnly = false,
  callbackRef,
  maxWidth,
  pinnedLeft,
  sticky,
  variant = 'default',
}: TableHeaderCellProps) => (
  <th
    colSpan={colSpan}
    rowSpan={rowSpan}
    scope={scope}
    className={classFilter([
      styles.base,
      variant === 'shrink' && styles.shrink,
      styles.textAlign[align],
      sticky && styles.headerSticky,
      (typeof pinnedLeft === 'number' || pinnedLeft === 'auto') &&
        styles.headerPinned,
      className,
    ])}
    style={maxWidth ? { width: maxWidth, left: pinnedLeft } : undefined}
    ref={callbackRef}
  >
    {screenReaderOnly ? (
      <ScreenReaderText>{children}</ScreenReaderText>
    ) : (
      <Text variant="captionMedium150" truncate>
        {children}
      </Text>
    )}
  </th>
);

const DragHandle = ({
  isDragging,
  ...props
}: Record<string, unknown> &
  Pick<HTMLAttributes<HTMLDivElement>, 'onMouseDown' | 'onDoubleClick'> & {
    isDragging: boolean;
  }) => (
  <div
    {...props}
    className={classFilter([
      styles.dragHandle,
      isDragging && styles.dragHandleDragging,
    ])}
    data-is-dragging={isDragging}
  />
);

TableHeaderCell.DragHandle = DragHandle;
